import React, { useMemo, useState } from 'react';
import { BellAlertIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useUserNotifications } from '../../hooks/useUserNotifications';
import { useAuth } from '../../contexts/AuthContext';
import { notificationService } from '../../services/notificationService';
import { canAccessServiceModule } from '../../utils/accessControl';
import { AppNotification } from '../../types/notifications';

const formatDate = (value: { toDate?: () => Date } | undefined): string => {
  if (!value || typeof value.toDate !== 'function') {
    return 'Brak daty';
  }
  try {
    return value.toDate().toLocaleString('pl-PL');
  } catch (_error) {
    return 'Brak daty';
  }
};

const AdminNotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile, claims } = useAuth();
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const { notifications, loading } = useUserNotifications({
    onlyUnread: false,
    maxResults: 80,
  });

  const filtered = useMemo(() => {
    const adminNotifications = notifications.filter((item) => item.recipientRole === 'admin');
    if (!showUnreadOnly) {
      return adminNotifications;
    }
    return adminNotifications.filter((item) => item.status === 'unread');
  }, [notifications, showUnreadOnly]);

  const unreadCount = useMemo(
    () =>
      notifications.filter((item) => item.recipientRole === 'admin' && item.status === 'unread')
        .length,
    [notifications]
  );

  const canAccessServiceProfile = canAccessServiceModule(userProfile, claims);

  const handleMarkAsRead = (notification: AppNotification) => {
    if (!currentUser?.uid) {
      return;
    }

    const markReadPromise = notificationService.markNotificationAsRead(notification.id);
    void markReadPromise.catch((error) => {
      console.error('Failed to mark admin notification as read:', error);
    });
  };

  const handleOpenNotification = async (notification: AppNotification): Promise<void> => {
    const { target } = notificationService.resolveNotificationNavigationTarget(notification, {
      canAccessServiceProfile,
      defaultTarget: '/admin',
    });

    navigate(target);
  };

  return (
    <section className="space-y-4">
      <header className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-ratuj-300/15 p-2 text-ratuj-200">
            <BellAlertIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-primary-light-100 sm:text-2xl">
              Powiadomienia
            </h1>
            <p className="mt-1 text-sm text-primary-light-300">
              Ten moduł korzysta z tego samego source of truth co bell i push.
            </p>
            <p className="mt-1 text-xs text-primary-light-500">Nieprzeczytane: {unreadCount}</p>
          </div>
        </div>
      </header>

      <div className="rounded-2xl border border-primary-dark-300/70 bg-primary-dark-200 p-3">
        <button
          type="button"
          onClick={() => setShowUnreadOnly((prev) => !prev)}
          className="inline-flex h-9 items-center justify-center rounded-lg border border-primary-dark-300 px-3 text-xs font-semibold text-primary-light-300 hover:text-primary-light-100"
        >
          {showUnreadOnly ? 'Pokaż wszystkie' : 'Pokaż tylko nieprzeczytane'}
        </button>
      </div>

      {loading && (
        <p className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-4 text-sm text-primary-light-300">
          Ładowanie powiadomień...
        </p>
      )}

      {!loading && filtered.length === 0 && (
        <p className="rounded-xl border border-primary-dark-300/70 bg-primary-dark-200 p-4 text-sm text-primary-light-300">
          Brak powiadomień do wyświetlenia.
        </p>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((item) => {
            const isUnread = item.status === 'unread';
            return (
              <article
                key={item.id}
                className={`rounded-2xl border p-4 ${
                  isUnread
                    ? 'border-ratuj-300/50 bg-primary-dark-200'
                    : 'border-primary-dark-300/70 bg-primary-dark-200/80'
                }`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-base font-semibold text-primary-light-100">
                        {item.title}
                      </h2>
                      {isUnread && (
                        <span className="rounded-full bg-ratuj-300/20 px-2 py-0.5 text-[11px] font-semibold text-ratuj-200">
                          Nowe
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-primary-light-300">{item.body}</p>
                    <p className="mt-2 text-xs text-primary-light-500">
                      {formatDate(item.createdAt)}
                    </p>
                  </div>

                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                    <button
                      type="button"
                      onClick={() => {
                        void handleOpenNotification(item);
                      }}
                      className="inline-flex h-9 items-center justify-center rounded-lg bg-ratuj-300 px-3 text-xs font-semibold text-primary-dark-100 hover:bg-ratuj-400"
                    >
                      Otwórz
                    </button>
                    {isUnread && (
                      <button
                        type="button"
                        onClick={() => handleMarkAsRead(item)}
                        className="inline-flex h-9 items-center justify-center gap-1 rounded-lg border border-primary-dark-300 px-3 text-xs font-semibold text-primary-light-300 hover:text-primary-light-100"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                        Oznacz jako przeczytane
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default AdminNotificationsPage;
